"use client"

import React, {useEffect, useMemo, useRef, useState} from "react";
import {Field} from "formik";
import {useTheme} from "@mui/material/styles";
import {Box, Checkbox, TextField, Typography, useMediaQuery} from "@mui/material";
import Image from "next/image";
import {apiPathName} from "@/lib/project-urls";
import Logo from "@/components/common/design/logo";
import FaIcon from "@/components/utilities/fa-icon";
import Card from "@mui/material/Card";
import InputErrorMessage from "@/components/utilities/input-error-message";
import {CustomSelectProps, OptionType} from "@/types/form";
import { replaceInvalidCharacters } from "@/utils/replace-invalid-characters";


export default function RHFSelect({
                                         label, inputSx,
                                         placeholder = "انتخاب کنید",
                                         name,
                                         options,
                                         image,
                                         sx,
                                         isMulti = false,
                                         fullWidth = false,
                                         disabled = false,
                                         loading = false,
                                     }: CustomSelectProps) {
    const theme = useTheme();
    const selectRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [highlighted, setHighlighted] = useState(0);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDarkMode = theme.palette.mode === 'dark';
    const [typingEnabled, setTypingEnabled] = useState(false);

    const filteredOptions = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return options;

        return options.filter((o) => {
            const label = (o.label ?? "").toLowerCase();
            const s = (o.search ?? "").toLowerCase();
            return label.includes(q) || s.includes(q);
        });
    }, [options, searchText]);


    useEffect(() => {
        setTypingEnabled(!isMobile);
    }, [isMobile]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearchText("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);


    useEffect(() => {
        if (!isOpen) return;
        const el = itemRefs.current[highlighted];
        if (el && menuRef.current) el.scrollIntoView({block: "nearest"});
    }, [highlighted, isOpen]);


    useEffect(() => {
        setHighlighted(0);
    }, [searchText, isOpen]);


    useEffect(() => {
        const body = document.body;
        if (isOpen) {
            body.style.overflow = "visible";
            body.style.touchAction = "auto";
        } else {
            body.style.overflow = "";
            body.style.touchAction = "";
        }
        return () => {
            body.style.overflow = "";
            body.style.touchAction = "";
        };
    }, [isOpen]);


    const handleKey = (
        e: React.KeyboardEvent<HTMLInputElement>,
        listLength: number,
        onEnter: () => void
    ) => {
        if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) setIsOpen(true);
        if (e.key === "Tab") {
            setIsOpen(false);
            setSearchText("");
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((p) => (p + 1 < listLength ? p + 1 : 0));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((p) => (p - 1 >= 0 ? p - 1 : Math.max(listLength - 1, 0)));
        } else if (e.key === "Home") {
            e.preventDefault();
            setHighlighted(0);
        } else if (e.key === "End") {
            e.preventDefault();
            setHighlighted(Math.max(listLength - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            onEnter();
        } else if (e.key === "Escape") {
            setIsOpen(false);
            setSearchText("");
        }
    };


    return (
        <Box position= "relative" sx = {{ width: fullWidth ? "100%" : "auto", height: "100%"}} ref = {selectRef} >
            {label && <Typography fontSize = {14} >{label}</Typography >}

            <Field name = {name} >
                {({field, form}: any) => {
                    const selectedValues = field.value || (isMulti ? [] : null);
                    const hasError = Boolean(form.touched[name] && form.errors[name]);
                    const errorMessage = typeof form.errors[name] === "string" ? form.errors[name] : "";
                    const displayValue = isMulti ? "" : selectedValues ? selectedValues.label : "";


                    const handleSelect = (option: OptionType) => {
                        let newValue: any;

                        if (isMulti) {
                            if (selectedValues.some((i: OptionType) => i.value === option.value)) {
                                newValue = selectedValues.filter((i: OptionType) => i.value !== option.value);
                            } else {
                                newValue = [...selectedValues, option];
                            }
                        } else {
                            newValue = option;
                            setIsOpen(false);
                        }

                        form.setFieldValue(name, newValue);
                        setSearchText("");
                        if (isMobile) setTypingEnabled(false);
                        setTimeout(() => inputRef.current?.focus(), 0);
                    };

                    const handleClear = () => {
                        form.setFieldValue(name, isMulti ? [] : null);
                        setSearchText("");
                        setTimeout(() => inputRef.current?.focus(), 0);
                    };


                    return (
                        <>
                            <Box
                                height="100%"
                                display="flex"
                                alignItems="center"
                                position="relative"
                                borderRadius={1}
                                border={hasError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`}
                                sx = {{
                                    "&:hover": {
                                        border:  `1px solid ${theme.palette.common.black}`,
                                    },

                                    "&:focus-within": {
                                        border: hasError
                                            ? `1px solid ${theme.palette.error.main}`
                                            : `1px solid ${theme.palette.grey[400]}`,
                                        background: theme.palette.common.white,
                                    },

                                    padding: "0px 10px 0px 0px",
                                    backgroundColor: disabled ? theme.palette.action.disabledBackground : theme.palette.grey[200],
                                    cursor: "pointer",
                                    ...sx,
                                }}

                                onClick={() => {
                                    if (disabled || loading) return;

                                    if (!isOpen) {
                                        setIsOpen(true);

                                        if (!isMobile) {
                                            setTypingEnabled(true);
                                            setTimeout(() => inputRef.current?.focus(), 0);
                                        } else {
                                            setTypingEnabled(false);
                                        }

                                        return;
                                    }

                                    if (isMobile && !typingEnabled) {
                                        setTypingEnabled(true);
                                        setTimeout(() => inputRef.current?.focus(), 0);
                                    }
                                }}
                            >
                                <TextField
                                    inputRef = {inputRef}
                                    fullWidth
                                    autoComplete = "off"
                                    value = {searchText || displayValue}
                                    placeholder = {placeholder}
                                    disabled = {disabled || loading}
                                    onChange = {(e) => setSearchText(replaceInvalidCharacters(e.target.value))}
                                    InputProps={{
                                        readOnly: !isOpen || (isMobile && !typingEnabled),
                                    }}
                                    inputProps={{
                                        inputMode: isMobile && !typingEnabled ? "none" : "text",
                                        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                                            if (isMobile && !typingEnabled) {
                                                e.target.blur();
                                            }
                                        },
                                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                                            handleKey(e, filteredOptions?.length, () => {
                                                const opt = filteredOptions[highlighted];
                                                if (opt) handleSelect(opt);
                                            }),
                                    }}
                                    variant = "outlined"
                                    data-borderless = "true"
                                    {...inputSx}
                                />

                                {selectedValues && (isMulti ? selectedValues.length > 0 : selectedValues) && (
                                    <Box
                                        onClick = {(e) => {
                                            e.stopPropagation();
                                            handleClear();
                                        }}
                                        alignItems = "center"
                                        display = "flex"
                                        mt = {.2}
                                    >
                                        <FaIcon fontSize = {16} color = {theme.palette.grey[400]} faIcon = "fa-close" />
                                    </Box >
                                )}

                                <Box sx = {{
                                    height: {md: "35%", xs: 20},
                                    width: "1px",
                                    backgroundColor: theme.palette.grey[300],
                                    mx: .8
                                }} />

                                <FaIcon
                                    fontSize = {14}
                                    color = {theme.palette.grey[400]}
                                    faIcon = "fa-chevron-down"
                                    sx = {{
                                        transition: "transform 0.2s",
                                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
                                    }}
                                />

                            </Box >

                            {isOpen && (
                                <Card
                                    ref = {menuRef}
                                    sx = {{
                                        position: "absolute",
                                        width: { md: isMulti ? "140%" : "100%", xs: "100%" },
                                        border: `1px solid ${theme.palette.divider}`,
                                        zIndex: 2000,
                                        maxHeight: 180,
                                        overflowY: "auto",
                                        WebkitOverflowScrolling: "touch",
                                        mt: { sm: 1.5, xs: 0 },
                                    }}
                                >
                                    {filteredOptions.length > 0 ? (
                                        filteredOptions.map((option, index) => {
                                            const isValidImage = option.image && option.image.trim().length > 2;
                                            const isExternal = isValidImage && (option?.image?.startsWith("http") || option?.image?.startsWith("/images/"));
                                            const imageSrc = isValidImage ? (isExternal ? option.image : `${apiPathName}/${option.image}`) : null;
                                            const active = index === highlighted;

                                            return (
                                                <Box
                                                    key = {option.value}
                                                    ref = {(el: HTMLDivElement | null) => {
                                                        itemRefs.current[index] = el;
                                                    }}
                                                    onMouseEnter = {() => setHighlighted(index)}
                                                    onClick = {() => handleSelect(option)}
                                                    display="flex"
                                                    alignItems="center"
                                                    bgcolor={active ? theme.palette.action.hover : theme.palette.common.white}
                                                    sx = {{
                                                        cursor: "pointer",
                                                        "&:hover": {backgroundColor: theme.palette.action.hover},
                                                    }}
                                                >
                                                    {isMulti && (
                                                        <Checkbox
                                                            checked = {selectedValues.some((i: OptionType) => i.value === option.value)}
                                                            icon = {<Box sx = {{
                                                                width: 22,
                                                                height: 22,
                                                                borderRadius: 2,
                                                                border: `2px solid ${theme.palette.divider}`
                                                            }} />
                                                        }
                                                            checkedIcon = {
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    justifyContent="center"
                                                                    bgcolor={theme.palette.primary.main}
                                                                    color={theme.palette.common.white}
                                                                    width={22}
                                                                    height={22}
                                                                    borderRadius={2}
                                                                >
                                                                    <FaIcon faIcon = "fa-check" fontSize = {13} variant = "solid" />
                                                                </Box >
                                                            }
                                                            sx = {{p: 0, mr: 0.5, ml: 1.5}}
                                                        />
                                                    )}
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        width="100%"
                                                        mx={1}
                                                        gap={1}
                                                        py={isMulti ? 0.5 : 1}
                                                        borderBottom={index + 1 !== filteredOptions.length ? `1px solid ${theme.palette.grey[200]}`
                                                            : undefined
                                                    }
                                                    >
                                                        {image && imageSrc ? (
                                                            <Image src = {imageSrc} alt = {option.label}
                                                                width = {36} height = {36} quality = {50}
                                                                style = {{
                                                                    mixBlendMode: isDarkMode ? undefined : "multiply",

                                                                }}

                                                            />
                                                        ) : (
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                width={38}
                                                                height={38}
                                                                fontSize={30}
                                                                bgcolor={theme.palette.common.white}
                                                                sx = {{
                                                                    opacity: 0.5,
                                                                }}
                                                            >
                                                                <Logo />
                                                            </Box >
                                                        )}
                                                        <Typography
                                                            fontSize = {isMulti ? 13 : 14} >{option.label}</Typography >
                                                    </Box >
                                                </Box >
                                            );
                                        })
                                    ) : (
                                        <Typography
                                            p={2}
                                            color={theme.palette.text.secondary}
                                            display="flex"
                                            justifyContent="center"
                                           >
                                            پیدا نشد
                                        </Typography >
                                    )}
                                </Card >
                            )}

                            <InputErrorMessage
                                error = {hasError}
                                errorMessage = {errorMessage}
                            />

                        </>
                    );
                }}
            </Field >
        </Box >
    );
};

